/**
 * DISPLAY build path — the Awareness & reach objective
 * (src/pages/google-ads/awareness/). Ad groups target audiences + content;
 * the creative is a Responsive Display Ad: text, landscape + square images and
 * logos that Google assembles into every banner size on the Display Network.
 *
 * Components are MODULE SCOPE on purpose: inline definitions remount on every
 * keystroke and lose focus.
 */
import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  Copy, Eye, Film, Globe, Image as ImageIcon, Layers, LayoutTemplate, MousePointerClick, Plus, Shuffle, SlidersHorizontal, Smartphone, Target,
  Trash2, Type,
} from 'lucide-react';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Select } from '@/components/Select';
import { MEDIA_FOLDERS } from '@/services/uploads.service';
import { DisplayBidding, GoogleAdsAssetRef, GoogleAdsCampaignSummary, googleAdsService } from '@/services/google-ads.service';
import { Label, Switch, Tag } from '@/components/google-ads/youtube/youtubeShared';
import { AGE_LABELS, AGE_RANGES, GENDER_LABELS, GENDERS, VIDEO_TOPICS, parseYoutubeId } from '@/pages/google-ads/googleAdsTargeting';
import {
  assetCache, ChipToggle, createAssetCached, ensureImageAssets, hostOf, IMAGE_SPECS, ImageDraft, ImageGrid, isHttpUrl, MoneyInput, Pub, StepIcon,
  toMicros, uid,
} from '@/components/google-ads/objectives/objectiveShared';
import {
  badVideoLines, ChoiceOption, CountedInput, IconBtn, Panel, PreviewFrame, Segmented, strengthOf, StrengthMeter, StrengthPill, StrengthRow,
  TagInput, textIssues, TextLines, validVideoIds, YouTubeList,
} from '@/components/google-ads/objectives/wizardChrome';

// ── bidding ──────────────────────────────────────────────────────────────

export const DISPLAY_BIDDING: ReadonlyArray<ChoiceOption<DisplayBidding>> = [
  { key: 'MANUAL_CPM', title: 'Viewable CPM', icon: Eye,
    desc: 'Pay per 1,000 impressions people actually see — the awareness default.', hint: 'Max CPM set on each ad group' },
  { key: 'MAXIMIZE_CLICKS', title: 'Maximize clicks', icon: MousePointerClick,
    desc: 'Google finds the most site visits your budget can buy.', hint: 'Optional max CPC cap' },
  { key: 'MANUAL_CPC', title: 'Manual CPC', icon: SlidersHorizontal,
    desc: 'You set the highest price per click on each ad group.', hint: 'Full control' },
  { key: 'MAXIMIZE_CONVERSIONS', title: 'Maximize conversions', icon: Target, needsConversions: true,
    desc: 'Bids toward people likely to buy, call or sign up.', hint: 'Optional target CPA' },
];
export const displayBiddingTitle = (b: DisplayBidding) => DISPLAY_BIDDING.find((o) => o.key === b)?.title ?? b;

export type BidMode = 'cpc' | 'cpm' | 'none';
export const bidModeForBidding = (b: DisplayBidding): BidMode => (b === 'MANUAL_CPM' ? 'cpm' : b === 'MANUAL_CPC' ? 'cpc' : 'none');
export const bidModeForCampaign = (biddingStrategyType?: string | null): BidMode =>
  (biddingStrategyType ?? '').startsWith('MANUAL_CPM') ? 'cpm' : (biddingStrategyType ?? '').startsWith('MANUAL_CPC') ? 'cpc' : 'none';
export const isDisplayCampaign = (c: GoogleAdsCampaignSummary) => c.advertisingChannelType === 'DISPLAY';

/** Button text on the ad. Empty = Google picks the best one per placement. */
export const RDA_CTAS = ['', 'Learn more', 'Shop now', 'Book now', 'Get quote', 'Contact us', 'Sign up', 'Subscribe', 'Order now', 'Apply now', 'Download', 'Visit site'];
export const RDA_LIMITS = {
  headlines: { min: 1, max: 5, chars: 30 },
  descriptions: { min: 1, max: 5, chars: 90 },
  longHeadline: 90,
  businessName: 25,
  /** Landscape + square images together. */
  images: 15,
  /** Square + landscape logos together. */
  logos: 5,
  videos: 5,
} as const;

// ── drafts ───────────────────────────────────────────────────────────────

export type RdaDraft = {
  id: string; name: string; finalUrl: string; businessName: string;
  headlines: string[]; longHeadline: string; descriptions: string[]; cta: string;
  marketingImages: ImageDraft[]; squareImages: ImageDraft[];
  /** 1:1 logos → square_logo_images. */
  squareLogos: ImageDraft[];
  /** 4:1 logos → logo_images. */
  wideLogos: ImageDraft[];
  videos: string[];
};
export type DisplayGroupDraft = {
  id: string; name: string; bid: string; ages: string[]; genders: string[]; topicIds: number[];
  keywords: string[]; placements: string[]; optimizedTargeting: boolean; ads: RdaDraft[];
};

/** Brand fields a new ad inherits from its neighbour (or the campaign's brand step). */
export type RdaBrand = Pick<RdaDraft, 'finalUrl' | 'businessName' | 'cta' | 'squareLogos' | 'wideLogos'>;

export const newRda = (n: number, brand?: Partial<RdaBrand>): RdaDraft => ({
  id: uid(), name: `Ad ${n}`, finalUrl: brand?.finalUrl ?? '', businessName: brand?.businessName ?? '',
  headlines: ['', '', ''], longHeadline: '', descriptions: ['', ''], cta: brand?.cta ?? '',
  marketingImages: [], squareImages: [], squareLogos: (brand?.squareLogos ?? []).map((i) => ({ ...i })), wideLogos: (brand?.wideLogos ?? []).map((i) => ({ ...i })),
  videos: [''],
});
export const newDisplayGroup = (n: number, brand?: Partial<RdaBrand>): DisplayGroupDraft => ({
  id: uid(), name: `Ad group ${n}`, bid: '1', ages: [], genders: [], topicIds: [], keywords: [], placements: [], optimizedTargeting: true,
  ads: [newRda(1, brand)],
});
const cloneImages = (xs: ImageDraft[]) => xs.map((i) => ({ ...i }));
export const cloneRda = (a: RdaDraft): RdaDraft => ({
  ...a, id: uid(), name: a.name ? `${a.name} (copy)` : '', headlines: [...a.headlines], descriptions: [...a.descriptions], videos: [...a.videos],
  marketingImages: cloneImages(a.marketingImages), squareImages: cloneImages(a.squareImages), squareLogos: cloneImages(a.squareLogos), wideLogos: cloneImages(a.wideLogos),
});
export const cloneDisplayGroup = (g: DisplayGroupDraft): DisplayGroupDraft => ({
  ...g, id: uid(), name: g.name ? `${g.name} (copy)` : '', ages: [...g.ages], genders: [...g.genders], topicIds: [...g.topicIds],
  keywords: [...g.keywords], placements: [...g.placements], ads: g.ads.map(cloneRda),
});
export const brandOf = (a: RdaDraft | undefined): Partial<RdaBrand> | undefined =>
  a && { finalUrl: a.finalUrl, businessName: a.businessName, cta: a.cta, squareLogos: a.squareLogos, wideLogos: a.wideLogos };

// ── placements ───────────────────────────────────────────────────────────

export type PlacementKind = 'site' | 'channel' | 'video';
export function placementKind(v: string): PlacementKind {
  if (/^UC[A-Za-z0-9_-]{20,}$/.test(v.trim())) return 'channel';
  if (parseYoutubeId(v)) return 'video';
  return 'site';
}
const SITE_RE = /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(\/\S*)?$/i;
export const placementProblem = (v: string): string | null =>
  placementKind(v) === 'site' && !SITE_RE.test(v.trim()) ? 'Not a website, YouTube channel id (UC…) or video URL' : null;
export const PlacementChip = ({ v }: { v: string }) => {
  const k = placementKind(v);
  return (
    <span className="inline-flex items-center gap-1">
      {k === 'site' ? <Globe size={10} className="shrink-0 text-text-muted" /> : <Film size={10} className="shrink-0 text-danger" />}
      {k === 'video' ? `video ${parseYoutubeId(v)}` : v.replace(/^https?:\/\//, '')}
    </span>
  );
};

// ── validation + strength ────────────────────────────────────────────────

const filled = (xs: string[]) => xs.map((x) => x.trim()).filter(Boolean);
const ready = (xs: ImageDraft[]) => xs.filter((x) => x.url);

export function rdaIssues(a: RdaDraft): string[] {
  const i: string[] = [];
  if (!isHttpUrl(a.finalUrl)) i.push('a landing page URL (https://…)');
  if (!a.businessName.trim()) i.push('a business name');
  else if (a.businessName.length > RDA_LIMITS.businessName) i.push(`a business name ≤ ${RDA_LIMITS.businessName} characters`);
  i.push(...textIssues('headlines', a.headlines, RDA_LIMITS.headlines.min, RDA_LIMITS.headlines.chars));
  if (!a.longHeadline.trim()) i.push('a long headline');
  else if (a.longHeadline.length > RDA_LIMITS.longHeadline) i.push(`a long headline ≤ ${RDA_LIMITS.longHeadline} characters`);
  i.push(...textIssues('descriptions', a.descriptions, RDA_LIMITS.descriptions.min, RDA_LIMITS.descriptions.chars));
  if (ready(a.marketingImages).length < 1) i.push('a landscape image');
  if (ready(a.squareImages).length < 1) i.push('a square image');
  // Google doesn't demand a logo on a responsive display ad, but Demand Gen (where Display campaigns are
  // being moved) does, and the migration drops ads without one.
  if (ready(a.squareLogos).length + ready(a.wideLogos).length < 1) i.push('a logo');
  if (a.marketingImages.length + a.squareImages.length > RDA_LIMITS.images) i.push(`at most ${RDA_LIMITS.images} images in total`);
  if (a.squareLogos.length + a.wideLogos.length > RDA_LIMITS.logos) i.push(`at most ${RDA_LIMITS.logos} logos in total`);
  if ([...a.marketingImages, ...a.squareImages, ...a.squareLogos, ...a.wideLogos].some((x) => !x.url)) i.push('uploads to finish');
  const bad = badVideoLines(a.videos);
  if (bad) i.push(`${bad} video link${bad === 1 ? '' : 's'} fixed (not a YouTube URL)`);
  return i;
}

export function displayGroupIssues(g: DisplayGroupDraft, bidMode: BidMode, withAds = true): string[] {
  const i: string[] = [];
  if (!g.name.trim()) i.push('a name');
  if (bidMode !== 'none' && toMicros(g.bid) <= 0) i.push(bidMode === 'cpm' ? 'a max CPM above zero' : 'a max CPC above zero');
  const badPlacements = g.placements.filter((p) => placementProblem(p)).length;
  if (badPlacements) i.push(`${badPlacements} placement${badPlacements === 1 ? '' : 's'} fixed`);
  if (withAds && g.ads.length === 0) i.push('at least one ad');
  return i;
}

/** Google's guidance for a responsive display ad: 5 headlines, 5 descriptions, 5 of each image shape, a logo. */
export function rdaStrengthRows(a: RdaDraft | null): StrengthRow[] {
  if (!a) return [];
  return [
    { label: 'Headlines', have: new Set(filled(a.headlines).map((h) => h.toLowerCase())).size, min: 1, target: 5, weight: 2 },
    { label: 'Long headline', have: a.longHeadline.trim() ? 1 : 0, min: 1, target: 1, weight: 1 },
    { label: 'Descriptions', have: new Set(filled(a.descriptions).map((d) => d.toLowerCase())).size, min: 1, target: 5, weight: 2 },
    { label: 'Landscape images', have: ready(a.marketingImages).length, min: 1, target: 5, weight: 2 },
    { label: 'Square images', have: ready(a.squareImages).length, min: 1, target: 5, weight: 2 },
    { label: 'Logos', have: ready(a.squareLogos).length + ready(a.wideLogos).length, min: 1, target: 2, weight: 1 },
    { label: 'Videos', have: validVideoIds(a.videos).length, min: 0, target: 2, weight: 0.5 },
  ];
}
export const rdaStrength = (a: RdaDraft | null) => strengthOf(rdaStrengthRows(a));

export function groupTargetingSummary(g: DisplayGroupDraft): string {
  const parts = [
    g.genders.length && g.genders.length < GENDERS.length ? g.genders.map((x) => GENDER_LABELS[x]).join(' / ') : null,
    g.ages.length && g.ages.length < AGE_RANGES.length ? `${g.ages.length} age band${g.ages.length === 1 ? '' : 's'}` : null,
    g.topicIds.length ? `${g.topicIds.length} topic${g.topicIds.length === 1 ? '' : 's'}` : null,
    g.keywords.length ? `${g.keywords.length} keyword${g.keywords.length === 1 ? '' : 's'}` : null,
    g.placements.length ? `${g.placements.length} placement${g.placements.length === 1 ? '' : 's'}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : 'Everyone on the Display Network';
}

// ── editors ──────────────────────────────────────────────────────────────

const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

export function DisplayGroupEditor({ group, onChange, bidMode, currency, disabled }: {
  group: DisplayGroupDraft; onChange: (p: Partial<DisplayGroupDraft>) => void; bidMode: BidMode; currency?: string | null; disabled?: boolean;
}) {
  const set = (p: Partial<DisplayGroupDraft>) => { if (!disabled) onChange(p); };
  return (
    <div className="space-y-4">
      <div className={bidMode !== 'none' ? 'grid grid-cols-1 gap-3 sm:grid-cols-[1fr_220px]' : ''}>
        <div>
          <Label>Ad group name</Label>
          <Input className="mt-1" placeholder="e.g. UAE · women 25–44 · beauty" value={group.name} disabled={disabled} onChange={(e) => set({ name: e.target.value })} />
        </div>
        {bidMode !== 'none' && (
          <div>
            <Label>{bidMode === 'cpm' ? 'Max CPM (per 1,000 viewable)' : 'Max CPC'}</Label>
            <div className="mt-1"><MoneyInput value={group.bid} onChange={(v) => set({ bid: v })} currency={currency} disabled={disabled} /></div>
          </div>
        )}
      </div>

      <Panel title="Audience" hint="Who sees the ads. Leave it open for the widest reach — Google's recommendation for awareness.">
        <div className="space-y-3">
          <div>
            <div className="mb-1.5 flex items-baseline justify-between">
              <span className="text-2xs font-semibold text-text-secondary">Age</span>
              <span className="text-2xs text-text-muted">{group.ages.length ? `${group.ages.length} selected` : 'All ages'}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {AGE_RANGES.map((a) => <ChipToggle key={a} small on={group.ages.includes(a)} label={AGE_LABELS[a]} onClick={() => set({ ages: toggle(group.ages, a) })} />)}
            </div>
          </div>
          <div>
            <div className="mb-1.5 flex items-baseline justify-between">
              <span className="text-2xs font-semibold text-text-secondary">Gender</span>
              <span className="text-2xs text-text-muted">{group.genders.length ? `${group.genders.length} selected` : 'All genders'}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {GENDERS.map((x) => <ChipToggle key={x} small on={group.genders.includes(x)} label={GENDER_LABELS[x]} onClick={() => set({ genders: toggle(group.genders, x) })} />)}
            </div>
          </div>
          <div className="flex items-start justify-between gap-3 rounded-lg bg-surface-alt/60 px-3 py-2.5">
            <div>
              <div className="text-xs font-medium text-text-primary">Optimized targeting</div>
              <p className="mt-0.5 text-2xs leading-4 text-text-muted">Lets Google also reach people likely to respond beyond your selections. Keep it on unless you need a strict audience.</p>
            </div>
            <Switch checked={group.optimizedTargeting} label="Optimized targeting" disabled={disabled} onCheckedChange={(v) => set({ optimizedTargeting: v })} />
          </div>
        </div>
      </Panel>

      <Panel title="Content — where the ads show" hint="Optional. Narrow delivery to matching pages, apps and videos. Every item you add narrows reach, so start broad.">
        <div className="space-y-3">
          <div>
            <div className="mb-1.5 flex items-baseline justify-between">
              <span className="text-2xs font-semibold text-text-secondary">Topics</span>
              <span className="text-2xs text-text-muted">{group.topicIds.length ? `${group.topicIds.length} selected` : 'Any topic'}</span>
            </div>
            <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto rounded-xl border border-border bg-surface-alt/40 p-2.5">
              {VIDEO_TOPICS.map((t) => <ChipToggle key={t.id} small on={group.topicIds.includes(t.id)} label={t.label} onClick={() => set({ topicIds: toggle(group.topicIds, t.id) })} />)}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div>
              <span className="text-2xs font-semibold text-text-secondary">Content keywords</span>
              <p className="mb-1.5 text-2xs text-text-muted">Pages about these words. Enter or paste a list.</p>
              <TagInput values={group.keywords} onChange={(v) => set({ keywords: v })} disabled={disabled} placeholder="e.g. skincare, bridal makeup" />
            </div>
            <div>
              <span className="text-2xs font-semibold text-text-secondary">Placements</span>
              <p className="mb-1.5 text-2xs text-text-muted">Websites, YouTube channel ids (UC…) or video URLs.</p>
              <TagInput values={group.placements} onChange={(v) => set({ placements: v })} disabled={disabled} placeholder="e.g. gulfnews.com"
                renderChip={(v) => <PlacementChip v={v} />} problem={placementProblem} />
            </div>
          </div>
        </div>
      </Panel>
    </div>
  );
}

export function RdaEditor({ ad, onChange, disabled }: {
  ad: RdaDraft; onChange: (p: Partial<RdaDraft> | ((cur: RdaDraft) => Partial<RdaDraft>)) => void; disabled?: boolean;
}) {
  const images = (field: 'marketingImages' | 'squareImages' | 'squareLogos' | 'wideLogos') =>
    (f: (cur: ImageDraft[]) => ImageDraft[]) => onChange((cur) => ({ [field]: f(cur[field]) }));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <Label>Landing page URL</Label>
          <Input className="mt-1" iconLeft={<Globe size={13} />} placeholder="https://yourbusiness.com/offer" value={ad.finalUrl} disabled={disabled}
            onChange={(e) => onChange({ finalUrl: e.target.value.trim() })} />
        </div>
        <div>
          <Label>Business name</Label>
          <CountedInput className="mt-1" value={ad.businessName} maxLen={RDA_LIMITS.businessName} placeholder="Your brand" disabled={disabled}
            onChange={(v) => onChange({ businessName: v })} ariaLabel="Business name" />
        </div>
        <div>
          <Label>Ad name <span className="font-normal text-text-muted">(internal)</span></Label>
          <Input className="mt-1" placeholder="e.g. Eid offer · v1" value={ad.name} disabled={disabled} onChange={(e) => onChange({ name: e.target.value })} />
        </div>
        <div>
          <Label>Call to action</Label>
          <Select className="mt-1 h-10 rounded-lg" value={ad.cta} disabled={disabled} onChange={(e) => onChange({ cta: e.target.value })}>
            {RDA_CTAS.map((c) => <option key={c || 'auto'} value={c}>{c || 'Automated — Google picks per placement'}</option>)}
          </Select>
        </div>
      </div>

      <Panel title="Ad copy" hint="Google mixes these into every size — short headlines for small banners, the long headline for big ones.">
        <div className="space-y-4">
          <TextLines label="Headlines" items={ad.headlines} onChange={(v) => onChange({ headlines: v })} min={RDA_LIMITS.headlines.min} max={RDA_LIMITS.headlines.max}
            maxLen={RDA_LIMITS.headlines.chars} disabled={disabled} cols={2}
            placeholder={['New season, new look', 'Free delivery in the UAE', 'Book your visit today', 'Up to 40% off this week', 'Loved by 5,000 clients']} />
          <div>
            <Label>Long headline</Label>
            <CountedInput className="mt-1.5" value={ad.longHeadline} maxLen={RDA_LIMITS.longHeadline} disabled={disabled} ariaLabel="Long headline"
              placeholder="Shown instead of a headline in larger formats" onChange={(v) => onChange({ longHeadline: v })} />
          </div>
          <TextLines label="Descriptions" items={ad.descriptions} onChange={(v) => onChange({ descriptions: v })} min={RDA_LIMITS.descriptions.min}
            max={RDA_LIMITS.descriptions.max} maxLen={RDA_LIMITS.descriptions.chars} disabled={disabled}
            placeholder={['Discover the collection everyone in Dubai is talking about.', 'Same-day delivery, easy returns and secure checkout.']} />
        </div>
      </Panel>

      <Panel title="Images & logos" hint="At least one landscape image, one square image and a logo. Up to 15 images and 5 logos in total — about 5 of each shape gives Google the most sizes to fill.">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <ImageGrid label="Landscape images" spec={IMAGE_SPECS.landscape} images={ad.marketingImages} min={1} max={Math.max(1, RDA_LIMITS.images - ad.squareImages.length)}
            folder={MEDIA_FOLDERS.googleDisplay} disabled={disabled} onChange={images('marketingImages')} />
          <ImageGrid label="Square images" spec={IMAGE_SPECS.square} images={ad.squareImages} min={1} max={Math.max(1, RDA_LIMITS.images - ad.marketingImages.length)}
            folder={MEDIA_FOLDERS.googleDisplay} disabled={disabled} onChange={images('squareImages')} />
          <ImageGrid label="Square logo" spec={IMAGE_SPECS.logo} images={ad.squareLogos} max={Math.max(0, RDA_LIMITS.logos - ad.wideLogos.length)}
            folder={MEDIA_FOLDERS.googleDisplay} disabled={disabled} onChange={images('squareLogos')} />
          <ImageGrid label="Landscape logo" spec={IMAGE_SPECS.logoWide} images={ad.wideLogos} max={Math.max(0, RDA_LIMITS.logos - ad.squareLogos.length)} optional
            folder={MEDIA_FOLDERS.googleDisplay} disabled={disabled} onChange={images('wideLogos')} />
        </div>
      </Panel>

      <Panel title="Videos" hint="Optional. YouTube videos let the ad also serve as a video in placements that support it. Public or unlisted only.">
        <YouTubeList items={ad.videos} onChange={(v) => onChange({ videos: v })} max={RDA_LIMITS.videos} disabled={disabled} />
      </Panel>
    </div>
  );
}

/** Ad draft inside a group card: thumbnail · name · strength · state · preview / duplicate / remove, then the editor. */
export function RdaDraftCard({ ad, index, focused, onFocus, onChange, onDuplicate, onRemove, made, state, disabled }: {
  ad: RdaDraft; index: number; focused: boolean; onFocus: () => void;
  onChange: (p: Partial<RdaDraft> | ((cur: RdaDraft) => Partial<RdaDraft>)) => void;
  onDuplicate?: () => void; onRemove?: () => void; made: boolean; state?: Pub; disabled?: boolean;
}) {
  const issues = rdaIssues(ad);
  const thumb = ad.marketingImages.find((i) => i.url)?.url ?? ad.squareImages.find((i) => i.url)?.url;
  return (
    <div onFocusCapture={onFocus} className={clsx('rounded-xl border p-3.5 transition-colors', focused ? 'border-primary/40 bg-primary-soft/25' : 'border-border bg-surface')}>
      <div className="mb-3 flex items-center gap-2">
        {state && state !== 'idle' ? <StepIcon s={state} />
          : thumb ? <img src={thumb} alt="" className="h-6 w-11 shrink-0 rounded object-cover" />
          : <ImageIcon size={14} className="shrink-0 text-text-muted" />}
        <span className="min-w-0 flex-1 truncate text-xs font-semibold text-text-primary">{ad.name.trim() || `Ad ${index + 1}`}</span>
        <StrengthPill s={rdaStrength(ad)} className="hidden sm:inline-flex" />
        {made ? <Tag tone="ok">created</Tag> : issues.length ? <Tag tone="warn">{issues.length} missing</Tag> : <Tag tone="ok">ready</Tag>}
        <Button variant="ghost" size="sm" className="h-6 px-2 text-2xs" onClick={onFocus}>Preview</Button>
        {onDuplicate && <IconBtn small title="Duplicate ad" onClick={onDuplicate} disabled={disabled}><Copy size={13} /></IconBtn>}
        {onRemove && !made && <IconBtn small danger title="Remove ad" onClick={onRemove} disabled={disabled}><Trash2 size={13} /></IconBtn>}
      </div>
      <RdaEditor ad={ad} onChange={onChange} disabled={made || disabled} />
    </div>
  );
}

/** Ad-group draft card (targeting + its ads) — shared by the campaign workspace and the "add ad groups" page. */
export function DisplayGroupCard({ group: g, index, open, onToggle, bidMode, currency, pub, madeGroup, madeAd, busy, onChange, onDuplicate, onRemove,
  activeAdId, onFocusAd, brand }: {
  group: DisplayGroupDraft; index: number; open: boolean; onToggle: () => void; bidMode: BidMode; currency?: string | null;
  pub: Record<string, Pub>; madeGroup: boolean; madeAd: (id: string) => boolean; busy: boolean;
  onChange: (p: Partial<DisplayGroupDraft> | ((cur: DisplayGroupDraft) => Partial<DisplayGroupDraft>)) => void;
  onDuplicate?: () => void; onRemove?: () => void; activeAdId?: string; onFocusAd: (id: string) => void; brand?: Partial<RdaBrand>;
}) {
  const bad = displayGroupIssues(g, bidMode).length > 0 || g.ads.some((a) => rdaIssues(a).length > 0);
  const patchAd = (aid: string, p: Partial<RdaDraft> | ((cur: RdaDraft) => Partial<RdaDraft>)) =>
    onChange((cur) => ({ ads: cur.ads.map((a) => (a.id === aid ? { ...a, ...(typeof p === 'function' ? p(a) : p) } : a)) }));
  return (
    <Card padded={false} className="overflow-hidden">
      <div className={clsx('flex items-center gap-2.5 px-4 py-3', open && 'border-b border-border')}>
        <button type="button" className="flex min-w-0 flex-1 items-center gap-2.5 text-left" onClick={onToggle} aria-expanded={open}>
          {pub[g.id] && pub[g.id] !== 'idle' ? <StepIcon s={pub[g.id]} /> : <Layers size={15} className="shrink-0 text-text-muted" />}
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-text-primary">{g.name.trim() || `Ad group ${index + 1}`}</div>
            <div className="truncate text-2xs text-text-muted">{groupTargetingSummary(g)} · {g.ads.length} ad{g.ads.length === 1 ? '' : 's'}</div>
          </div>
        </button>
        {madeGroup ? <Tag tone="ok">created</Tag> : bad ? <Tag tone="warn">incomplete</Tag> : <Tag tone="ok">ready</Tag>}
        {onDuplicate && <IconBtn title="Duplicate ad group" onClick={onDuplicate} disabled={busy}><Copy size={14} /></IconBtn>}
        {onRemove && !madeGroup && <IconBtn danger title="Remove ad group" onClick={onRemove} disabled={busy}><Trash2 size={14} /></IconBtn>}
      </div>
      {open && (
        <div className="space-y-4 p-4">
          <DisplayGroupEditor group={g} onChange={onChange} bidMode={bidMode} currency={currency} disabled={madeGroup || busy} />
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-text-primary"><LayoutTemplate size={13} className="text-text-muted" /> Ads in this group</div>
            {g.ads.map((a, k) => (
              <RdaDraftCard key={a.id} ad={a} index={k} focused={activeAdId === a.id} onFocus={() => onFocusAd(a.id)}
                onChange={(p) => patchAd(a.id, p)} made={madeAd(a.id)} state={pub[a.id]} disabled={busy}
                onDuplicate={() => { const c = cloneRda(a); onChange((cur) => { const i = cur.ads.findIndex((x) => x.id === a.id); return { ads: [...cur.ads.slice(0, i + 1), c, ...cur.ads.slice(i + 1)] }; }); onFocusAd(c.id); }}
                onRemove={g.ads.length > 1 ? () => onChange((cur) => ({ ads: cur.ads.filter((x) => x.id !== a.id) })) : undefined} />
            ))}
            <Button variant="secondary" size="sm" iconLeft={<Plus size={14} />} disabled={busy}
              onClick={() => { const a = newRda(g.ads.length + 1, brandOf(g.ads[g.ads.length - 1]) ?? brand); onChange((cur) => ({ ads: [...cur.ads, a] })); onFocusAd(a.id); }}>
              Add another ad
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

// ── preview ──────────────────────────────────────────────────────────────

type Format = 'native' | 'banner' | 'mobile' | 'text';
const FORMATS: ReadonlyArray<{ value: Format; label: string; icon: typeof Eye }> = [
  { value: 'native', label: 'Native', icon: LayoutTemplate },
  { value: 'banner', label: 'Banner', icon: ImageIcon },
  { value: 'mobile', label: 'Mobile', icon: Smartphone },
  { value: 'text', label: 'Text', icon: Type },
];
const pick = <T,>(xs: T[], n: number): T | undefined => (xs.length ? xs[n % xs.length] : undefined);

const PreviewLogo = ({ logo, brand, size = 'h-9 w-9' }: { logo?: string; brand: string; size?: string }) => (
  <span className={clsx('flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#e8f0fe] text-xs font-bold uppercase text-[#1a73e8]', size)}>
    {logo ? <img src={logo} alt="" className="h-full w-full bg-white object-contain" /> : brand.charAt(0)}
  </span>
);
const PreviewImg = ({ src, className }: { src?: string; className: string }) => (
  <div className={clsx('overflow-hidden bg-[#f1f3f4] dark:bg-[#2a2a2a]', className)}>
    {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-[#9aa0a6]"><ImageIcon size={16} /></div>}
  </div>
);

/**
 * Four of the layouts Google builds from one responsive display ad, driven by
 * the ad being edited. Shuffle rotates the headline / description / image mix
 * the way Google does per placement.
 */
export function RdaPreview({ ad }: { ad: RdaDraft | null }) {
  const [format, setFormat] = useState<Format>('native');
  const [shift, setShift] = useState(0);
  const strengthRows = useMemo(() => rdaStrengthRows(ad), [ad]);
  const strength = useMemo(() => strengthOf(strengthRows), [strengthRows]);
  const hs = filled(ad?.headlines ?? []);
  const ds = filled(ad?.descriptions ?? []);
  const landscape = ready(ad?.marketingImages ?? []).map((i) => i.url);
  const square = ready(ad?.squareImages ?? []).map((i) => i.url);
  const logo = ready(ad?.squareLogos ?? [])[0]?.url ?? ready(ad?.wideLogos ?? [])[0]?.url;
  const headline = pick(hs, shift) ?? 'Your headline';
  const longHeadline = ad?.longHeadline.trim() || headline;
  const desc = pick(ds, shift) ?? 'Your description appears here.';
  const host = ad?.finalUrl && isHttpUrl(ad.finalUrl) ? hostOf(ad.finalUrl) : 'yourbusiness.com';
  const brand = ad?.businessName.trim() || host;
  const cta = ad?.cta || 'Learn more';
  const wide = pick(landscape, shift) ?? pick(square, shift);
  const sq = pick(square, shift) ?? pick(landscape, shift);
  const shell = 'overflow-hidden rounded-xl border border-border bg-white text-[#202124] shadow-soft dark:bg-[#1f1f1f] dark:text-[#e8eaed]';
  const muted = 'text-[#5f6368] dark:text-[#9aa0a6]';

  return (
    <PreviewFrame
      title="Preview · Display Network"
      controls={(
        <button type="button" onClick={() => setShift((n) => n + 1)} title="Show another combination" aria-label="Show another combination"
          className="flex h-6 w-6 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary"><Shuffle size={13} /></button>
      )}
      footer={ad ? <StrengthMeter s={strength} rows={strengthRows} /> : undefined}
      note="Indicative only — Google builds hundreds of sizes from these assets and picks per placement, device and person.">
      {!ad ? <p className="py-10 text-center text-xs text-text-muted">Add an ad to see it previewed here.</p> : (
        <>
          <div className="mb-3 flex justify-center"><Segmented options={FORMATS} value={format} onChange={setFormat} size="xs" ariaLabel="Preview format" /></div>

          {format === 'native' && (
            <div className={shell}>
              <PreviewImg src={wide} className="aspect-[1.91/1] w-full" />
              <div className="flex items-start gap-3 p-3">
                <PreviewLogo logo={logo} brand={brand} />
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 text-[14px] font-semibold leading-snug">{headline}</div>
                  <div className={clsx('mt-0.5 line-clamp-2 text-[12px] leading-snug', muted)}>{desc}</div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className={clsx('truncate text-[11px]', muted)}>Ad · {brand}</span>
                    <span className="shrink-0 rounded-full bg-[#1a73e8] px-3 py-1 text-[11px] font-semibold text-white">{cta}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {format === 'banner' && (
            <div className="flex justify-center">
              <div className={clsx(shell, 'w-[288px]')} style={{ aspectRatio: '300 / 250' }}>
                <div className="flex h-full flex-col">
                  <PreviewImg src={wide} className="h-[58%] w-full" />
                  <div className="flex flex-1 flex-col justify-between p-2.5">
                    <div className="flex items-start gap-2">
                      <PreviewLogo logo={logo} brand={brand} size="h-7 w-7" />
                      <div className="min-w-0 flex-1 text-[13px] font-semibold leading-snug line-clamp-2">{longHeadline}</div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className={clsx('truncate text-[10px]', muted)}>{brand}</span>
                      <span className="shrink-0 rounded bg-[#1a73e8] px-2.5 py-1 text-[10px] font-semibold text-white">{cta} ›</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {format === 'mobile' && (
            <div className="mx-auto max-w-[300px] rounded-[22px] border-[6px] border-[#202124] bg-[#f8f9fa] p-2 dark:border-[#3c4043] dark:bg-[#121212]">
              <div className="mb-2 space-y-1.5 px-1">{[80, 64, 72].map((w) => <div key={w} className="h-1.5 rounded bg-[#dadce0] dark:bg-[#3c4043]" style={{ width: `${w}%` }} />)}</div>
              <div className={clsx(shell, 'flex items-center gap-2 p-2')}>
                <PreviewImg src={sq} className="h-14 w-14 shrink-0 rounded-md" />
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 text-[12px] font-semibold leading-snug">{headline}</div>
                  <div className={clsx('truncate text-[10px]', muted)}>Ad · {brand}</div>
                </div>
                <span className="shrink-0 rounded-full bg-[#1a73e8] px-2.5 py-1 text-[10px] font-semibold text-white">{cta}</span>
              </div>
              <div className="mt-2 space-y-1.5 px-1">{[70, 86].map((w) => <div key={w} className="h-1.5 rounded bg-[#dadce0] dark:bg-[#3c4043]" style={{ width: `${w}%` }} />)}</div>
            </div>
          )}

          {format === 'text' && (
            <div className={clsx(shell, 'p-3.5')}>
              <div className="flex items-center gap-2">
                <PreviewLogo logo={logo} brand={brand} size="h-7 w-7" />
                <div className="min-w-0 leading-tight">
                  <div className="truncate text-[12px] font-semibold">{brand}</div>
                  <div className={clsx('truncate text-[10px]', muted)}>Ad · {host}</div>
                </div>
              </div>
              <div className="mt-2 text-[16px] font-semibold leading-snug text-[#1a0dab] dark:text-[#8ab4f8]">{longHeadline}</div>
              <div className={clsx('mt-1 text-[12px] leading-snug', muted)}>{desc}</div>
              <div className="mt-3 flex justify-end"><span className="rounded-full border border-[#dadce0] px-3 py-1 text-[11px] font-semibold text-[#1a73e8] dark:border-[#3c4043]">{cta}</span></div>
            </div>
          )}
        </>
      )}
    </PreviewFrame>
  );
}

// ── publish ──────────────────────────────────────────────────────────────

type ImgField = 'marketingImages' | 'squareImages' | 'squareLogos' | 'wideLogos';

/**
 * Uploads → Google assets (cached per account) → responsive display ad. Asset
 * ids are written back so a retry never re-uploads. Square logos go to
 * square_logo_images and 4:1 logos to logo_images — Google rejects a square
 * image in logo_images.
 */
export async function publishRda(
  accountUid: string, adGroupId: string, ad: RdaDraft,
  writeAsset: (field: ImgField, id: string, resourceName: string) => void,
): Promise<{ resourceName: string }> {
  const create = createAssetCached(accountUid, (b) => googleAdsService.display.createAsset(accountUid, b) as Promise<GoogleAdsAssetRef>);
  const marketing = await ensureImageAssets(ad.marketingImages, create, (id, rn) => writeAsset('marketingImages', id, rn));
  const square = await ensureImageAssets(ad.squareImages, create, (id, rn) => writeAsset('squareImages', id, rn));
  const squareLogos = await ensureImageAssets(ad.squareLogos, create, (id, rn) => writeAsset('squareLogos', id, rn));
  const wideLogos = await ensureImageAssets(ad.wideLogos, create, (id, rn) => writeAsset('wideLogos', id, rn));
  const videos: string[] = [];
  for (const v of validVideoIds(ad.videos)) {
    const key = `${accountUid}:yt:${v}`;
    let rn = assetCache.get(key);
    if (!rn) { rn = (await googleAdsService.display.createAsset(accountUid, { youtubeVideoId: v })).resourceName; assetCache.set(key, rn); }
    videos.push(rn);
  }
  return googleAdsService.display.createAd(accountUid, {
    adGroupId, finalUrls: [ad.finalUrl.trim()], businessName: ad.businessName.trim(),
    headlines: filled(ad.headlines), longHeadline: ad.longHeadline.trim(), descriptions: filled(ad.descriptions),
    marketingImages: marketing, squareMarketingImages: square,
    squareLogoImages: squareLogos.length ? squareLogos : undefined,
    logoImages: wideLogos.length ? wideLogos : undefined,
    youtubeVideos: videos.length ? videos : undefined,
    callToActionText: ad.cta || undefined, status: 'PAUSED',
  });
}

/** Immutable write of an asset resource name into one of an ad's image lists. */
export const writeRdaAsset = (field: ImgField, id: string, rn: string) => (cur: RdaDraft): Partial<RdaDraft> => ({
  [field]: cur[field].map((im) => (im.id === id ? { ...im, resourceName: rn } : im)),
});

/** Ad-group create payload from a draft (shared by the wizard and the "add ad groups" page). */
export const displayGroupPayload = (campaignId: string, g: DisplayGroupDraft, bidMode: BidMode) => {
  const sites = g.placements.filter((p) => placementKind(p) === 'site').map((p) => p.trim().replace(/^https?:\/\//, '').replace(/\/$/, ''));
  const channels = g.placements.filter((p) => placementKind(p) === 'channel').map((p) => p.trim());
  const videos = g.placements.filter((p) => placementKind(p) === 'video').map((p) => parseYoutubeId(p) as string);
  const ages = g.ages.length && g.ages.length < AGE_RANGES.length ? g.ages : [];
  const genders = g.genders.length && g.genders.length < GENDERS.length ? g.genders : [];
  const hasTargeting = ages.length || genders.length || g.topicIds.length || sites.length || channels.length || videos.length || g.keywords.length;
  return {
    campaignId, name: g.name.trim(), status: 'PAUSED' as const,
    cpcBidMicros: bidMode === 'cpc' ? toMicros(g.bid) : undefined,
    cpmBidMicros: bidMode === 'cpm' ? toMicros(g.bid) : undefined,
    optimizedTargetingEnabled: g.optimizedTargeting,
    targeting: hasTargeting ? {
      ageRanges: ages.length ? ages : undefined, genders: genders.length ? genders : undefined,
      topicIds: g.topicIds.length ? g.topicIds : undefined,
      placementUrls: sites.length ? sites : undefined, youtubeChannelIds: channels.length ? channels : undefined, youtubeVideoIds: videos.length ? videos : undefined,
      keywords: g.keywords.length ? g.keywords : undefined,
    } : undefined,
  };
};

/** Count of images across an ad, for review lines. */
export const rdaImageCount = (a: RdaDraft) => ready(a.marketingImages).length + ready(a.squareImages).length;
export const rdaLogoCount = (a: RdaDraft) => ready(a.squareLogos).length + ready(a.wideLogos).length;

export const Muted = ({ children }: { children: React.ReactNode }) => <span className="text-text-muted">{children}</span>;
